import { DBSCAN } from "density-clustering";
import { MIN_CITATION_WEIGHT } from "@components/overview/components/top-sources/TopSources";
const X_DOMAIN = [0, 100];
const SPLIT_X = 50;
const HALF_MARGIN = 5;
const HALF_GAP = 2;
const LOW_X_RANGE = [HALF_MARGIN, SPLIT_X - HALF_GAP];
const HIGH_X_RANGE = [SPLIT_X + HALF_GAP, 100 - HALF_MARGIN];
const Y_PADDING = 3;
const EMPTY_METRICS = {
    data: [],
    xDomain: X_DOMAIN,
    yDomain: [-60, 60],
    splitX: SPLIT_X,
    splitY: 0,
};
const normalizeStatus = (status) => (status !== null && status !== void 0 ? status : "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "-");
const isHighWeightStatus = (status) => {
    const normalized = normalizeStatus(status);
    return normalized === "maintain" || normalized === "priority-fix";
};
const isImprovementStatus = (status) => {
    const normalized = normalizeStatus(status);
    return normalized === "improve" || normalized === "priority-fix";
};
const mapIntoRange = (values, [lo, hi]) => {
    const mapped = new Map();
    if (!values.length)
        return mapped;
    const min = Math.min(...values);
    const max = Math.max(...values);
    values.forEach((value) => {
        mapped.set(value, min === max ? (lo + hi) / 2 : lo + ((value - min) / (max - min)) * (hi - lo));
    });
    return mapped;
};
export const computeQuadrantChartMetrics = (topSources) => {
    if (!(topSources === null || topSources === void 0 ? void 0 : topSources.length))
        return EMPTY_METRICS;
    const sources = topSources.filter((s) => Number.isFinite(s.citationWeight) && s.citationWeight >= MIN_CITATION_WEIGHT && Number.isFinite(s.gap));
    if (!sources.length)
        return EMPTY_METRICS;
    const lowX = mapIntoRange(sources.filter((s) => !isHighWeightStatus(s.status)).map((s) => s.citationWeight), LOW_X_RANGE);
    const highX = mapIntoRange(sources.filter((s) => isHighWeightStatus(s.status)).map((s) => s.citationWeight), HIGH_X_RANGE);
    const data = sources.map((s) => {
        const magnitude = Math.abs(s.gap) || 0.1;
        const x = isHighWeightStatus(s.status) ? highX.get(s.citationWeight) : lowX.get(s.citationWeight);
        return {
            name: s.source,
            x: x !== null && x !== void 0 ? x : SPLIT_X,
            y: isImprovementStatus(s.status) ? -magnitude : magnitude,
            status: s.status,
            rawX: s.citationWeight,
            rawGap: s.gap,
        };
    });
    const yExtent = Math.max(...data.map((p) => Math.abs(p.y))) + Y_PADDING;
    return {
        data,
        xDomain: X_DOMAIN,
        yDomain: [-yExtent, yExtent],
        splitX: SPLIT_X,
        splitY: 0,
    };
};
export const CLUSTER_RADIUS_PX = 64;
export const CLUSTER_Y_STRETCH = 3.5;
export const MIN_CLUSTER_POINTS = 2;
export const clusterByPixelDistance = (positions, radiusPx = CLUSTER_RADIUS_PX, yStretch = CLUSTER_Y_STRETCH, minPoints = MIN_CLUSTER_POINTS) => {
    if (positions.length === 0)
        return { clusters: [], singletons: [] };
    // A non-positive radius means clustering off, not "cluster only what overlaps exactly":
    // DBSCAN treats a zero distance as within range, so identical points would still collapse
    // into a badge. A print target has no hover to open one, so every point must stay itself.
    if (radiusPx <= 0) {
        return { clusters: [], singletons: positions.map((_, index) => index) };
    }
    const dataset = positions.map((p) => [p.x, p.y * yStretch]);
    const dbscan = new DBSCAN();
    const rawClusters = dbscan.run(dataset, radiusPx, minPoints);
    const clusters = rawClusters.map((members) => {
        const cx = members.reduce((sum, i) => sum + positions[i].x, 0) / members.length;
        const cy = members.reduce((sum, i) => sum + positions[i].y, 0) / members.length;
        return { members, cx, cy };
    });
    return { clusters, singletons: dbscan.noise };
};
export const LABEL_CHAR_WIDTH_PX = 7.5;
export const LABEL_H_GAP_PX = 8;
export const LABEL_HEIGHT_PX = 15;
export const LABEL_V_OFFSET_PX = 10;
export const MAX_LABEL_CHARS = 10;
export const truncateLabel = (name) => name.length > MAX_LABEL_CHARS ? `${name.slice(0, MAX_LABEL_CHARS)}…` : name;
const labelBox = (label, side, gapPx) => {
    const x0 = label.x - label.halfWidth - gapPx / 2;
    const x1 = label.x + label.halfWidth + gapPx / 2;
    if (side === "below") {
        const y0 = label.y + LABEL_V_OFFSET_PX;
        return { x0, x1, y0, y1: y0 + LABEL_HEIGHT_PX };
    }
    const y1 = label.y - LABEL_V_OFFSET_PX;
    return { x0, x1, y0: y1 - LABEL_HEIGHT_PX, y1 };
};
const overlaps = (a, b) => a.x0 < b.x1 && b.x0 < a.x1 && a.y0 < b.y1 && b.y0 < a.y1;
export const assignLabelSides = (labels, gapPx = LABEL_H_GAP_PX) => {
    const order = labels.map((_, i) => i).sort((a, b) => labels[a].x - labels[b].x);
    const sides = labels.map(() => "below");
    const placed = [];
    order.forEach((i) => {
        const below = labelBox(labels[i], "below", gapPx);
        const above = labelBox(labels[i], "above", gapPx);
        if (!placed.some((box) => overlaps(below, box))) {
            sides[i] = "below";
            placed.push(below);
        }
        else if (!placed.some((box) => overlaps(above, box))) {
            sides[i] = "above";
            placed.push(above);
        }
        else {
            sides[i] = "below";
            placed.push(below);
        }
    });
    return sides;
};
